// @flow
// Module dependencies
import Logging from 'app/common/lib/Logging';
import P from 'app/common/lib/P';
import Pc from 'app/common/lib/Pc';
import urlUtil from 'app/common/lib/urlUtil';

let window;
if (typeof window === 'undefined') {
  window = global;
}

// GA tracking queue
window._gaq = window._gaq || [];

const gaLog = Logging.getLogger('GoogleAnalytics');

let _accountNumbers = [];

const gaqPush = function(params) {
  gaLog.debug('gaqPush', params);

  if (!_accountNumbers.length) {
    return;
  }

  // Support multiple GA accounts. See:
  // http://www.lunametrics.com/blog/2009/02/26/pitfalls-tracking-multiple-accounts-ga/
  // TODO(chris): Investigate whether we need two sets of cookies in order for GA to track accurately:
  // http://stackoverflow.com/questions/2651834/google-analytics-async-tracking-with-two-accounts
  for (let i = 0; i < _accountNumbers.length; i += 1) {
    const accountNumber = _accountNumbers[i];
    declare var window: { _gaq: any[] };
    window._gaq.push(['_setAccount', accountNumber]);
    window._gaq.push(params);
  }
};

const initializeGA = function(accountNumbers: Array<string>) {
  _accountNumbers = accountNumbers;

  if (!accountNumbers.length) {
    return;
  }

  const ga = document.createElement('script');
  ga.type = 'text/javascript';
  ga.async = true;
  ga.src =
    (document.location.protocol === 'https:' ? 'https://ssl' : 'http://www') +
    '.google-analytics.com/ga.js';
  (
    document.getElementsByTagName('head')[0] || document.getElementsByTagName('body')[0]
  ).appendChild(ga);

  if (Pc.DEBUG) {
    gaqPush(['_setSampleRate', 100]);
    gaqPush(['_setSiteSpeedSampleRate', 100]);
  }
};

// https://developers.google.com/analytics/devguides/collection/gajs/gaTrackingCustomVariables
const CustomVarScope = {
  // the client that visits the site, such as the browser or mobile phone operated by a person.
  VISITOR: 1,
  // the period of time during which the visitor is active on the site.
  SESSION: 2,
  // This is typically characterized by a pageview, but it can include: a pageview, an event (e.g. click on a movie button)
  PAGE: 3,
};

const trackGALoggedInUser = function(loggedIn) {
  const value = loggedIn ? 'logged in' : 'logged out';
  gaqPush(['_setCustomVar', 1, 'is_logged_in', value, CustomVarScope.SESSION]);
};

// TODO(jchan): Use this to log to our database instead of using GA to store this
// info; want to keep this method around so that I can reuse it.
//
// var trackUserViewportSize = function() {
//     var width = window.innerWidth || document.body.clientWidth;
//     var height = window.innerHeight || document.body.clientHeight;
//     width  = Math.round(width / 100) * 100;
//     height = Math.round(height / 100) * 100;

//     // Track width x height combinations;
//     ga.trackEvent('Viewport', 'Size', width + 'x' + height, undefined, true);
//     // track width as numeric value (to calculate average width)
//     ga.trackEvent('Viewport', 'Width', width + 'x' + height, width, true);
//     // track height as numeric value (to calculate average height)
//     ga.trackEvent('Viewport', 'Height', width + 'x' + height, height, true);
// };

const ga = {};

ga.init = function(gaAccountNumbers: Array<string>, isLoggedIn: boolean) {
  initializeGA(gaAccountNumbers);
  trackGALoggedInUser(isLoggedIn);
};

type SterlingGAOptionsType = {
  isLoggedIn: boolean,
  advertiser: { id: string },
  adminUser: string,
  viewingUser: { id: string },
};
ga.initSterling = function(
  gaAccountNumbers: Array<string>,
  { isLoggedIn, advertiser, adminUser, viewingUser }: SterlingGAOptionsType,
) {
  initializeGA(gaAccountNumbers);
  if (!isLoggedIn) {
    gaqPush(['_setCustomVar', 1, 'is_logged_in', 'logged out', CustomVarScope.PAGE]);
  } else {
    if (adminUser) {
      gaqPush(['_setCustomVar', 1, 'adminUser', adminUser.toString(), CustomVarScope.PAGE]);
    } else if (viewingUser && viewingUser.id) {
      gaqPush(['_setCustomVar', 1, 'viewingUser', viewingUser.id.toString(), CustomVarScope.PAGE]);
    } else {
      // Shouldn't ever get here, but we can figure out if they do
      gaqPush(['_setCustomVar', 1, 'is_logged_in', 'logged in', CustomVarScope.PAGE]);
    }

    if (advertiser && advertiser.id) {
      gaqPush(['_setCustomVar', 3, 'advertiserId', advertiser.id, CustomVarScope.PAGE]);
    }
  }
};

/**
 * Get the current URL, scrubbed and appropriate to send to
 * GA for tracking a page view.
 * We intentionally don't send the hostname because it makes URLs longer and
 * isn't useful.
 *
 * @param {string} pathname
 * @param {string} search A window.location.search value (includes "?" at the beginning if not empty)
 * @return {string}
 */
const getTrackGAPageviewUrl = function(pathname, search) {
  let scrubbedUrl = pathname;

  // Although GA doesn't display the query string in most reports, there
  // are some report types that use "q" query string value.  Strip all query
  // string values except for "q" because there may be sensitive information
  // in there (e.g. "email=").
  const parsedQueryString = urlUtil.getQueryStringParams(search);
  const q = parsedQueryString.q;
  if (q) {
    scrubbedUrl += '?' + urlUtil.getQueryStringFromObject({ q });
  }
  return scrubbedUrl;
};

/**
 * @param {string} pageName
 */
// $FlowFixMe: new errors found from flow 0.96 upgrade
ga.trackPageview = function(pageName) {
  gaqPush(['_setCustomVar', 2, 'page_name', pageName, CustomVarScope.PAGE]);
  declare var window: Object;
  gaqPush([
    '_trackPageview',
    getTrackGAPageviewUrl(window.location.pathname, window.location.search),
  ]);
  gaqPush(['_setPageGroup', 1, pageName]);
};

/**
 * @param {string} category The name you supply for the group of objects you want to track.
 * @param {string=} action A string that is uniquely paired with each category, and commonly
 *      used to define the type of user interaction for the web object.
 * @param {string=} label An optional string to provide additional dimensions to the event data.
 * @param {number=} value An integer that you can use to provide numerical data about the user event.
 * @param {boolean=} noninteraction An optional boolean value that if true indicates that the event hit
 *      will not be used in bounce-rate calculation.
 */
ga.trackEvent = function(
  category: string,
  action: string,
  label?: string,
  value?: Number,
  noninteraction?: *,
) {
  gaqPush(['_trackEvent', category, action, label, value, noninteraction]);
};

/**
 * @param {string} category
 * @param {string} type
 * @param {string} event
 */
ga.trackCategoryEvent = function(category: string, type: string, event: string) {
  ga.trackEvent(category + '_' + type, event);
};

// eslint-disable-next-line pinterest/no-referencing-p-properties
export default P.expose('ga', ga);
